/**
 * Bills coming due on cards and loans, from the latest statement of each open
 * account: the due date, minimum or amount due, and AutoPay as the statement
 * prints them. A statement is the only source, so a bill is not known to be
 * paid until the next statement arrives; the person can mark one paid sooner.
 */
import type { Bills } from './extraction'
import type { Row } from './google/sheets'

const BILL_KINDS = new Set(['credit', 'mortgage', 'auto_loan', 'student_loan'])

const str = (v: unknown) => (v === null || v === undefined ? '' : String(v).trim())
const num = (v: unknown) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v))

export interface Bill {
  /** account_id and due date: a newer statement is a new bill. */
  key: string
  account_id: string
  name: string
  kind: string
  due: string
  /** Days from today to the due date; negative once it has passed. */
  days: number
  minimum: number | null
  /** What the statement says is owed, as a positive amount. */
  owed: number | null
  autopay: 'on' | 'off' | null
  /** The statement's date. */
  statement: string
}

/** Bank and product without saying either twice: "Apple Card", "Rocket Mortgage", "Citi Costco Anywhere Visa". */
export function billName(institution: string, name: string): string {
  const lower = (s: string) => s.toLowerCase()
  if (!name || lower(institution).endsWith(lower(name))) return institution
  if (!institution || lower(name).startsWith(lower(institution))) return name
  return `${institution} ${name}`
}

function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000)
}

/** The latest balance row of each open card or loan. */
function latestRows(tables: { accounts: Row[]; balances: Row[] }): { account: Row; row: Row }[] {
  const open = tables.accounts.filter((a) => BILL_KINDS.has(str(a.kind)) && !str(a.closed))
  return open.flatMap((account) => {
    const id = str(account.account_id)
    const row = tables.balances
      .filter((b) => str(b.account_id) === id)
      .sort((a, b) => str(a.as_of).localeCompare(str(b.as_of)))
      .at(-1)
    return row ? [{ account, row }] : []
  })
}

/**
 * Bills due from `behind` days ago to `ahead` days from today, soonest first.
 * One that has passed stays a few days, in case it was missed; after that the
 * next statement is the better signal.
 */
export function billsDue(tables: { accounts: Row[]; balances: Row[] }, today: string, ahead = 21, behind = 10): Bill[] {
  return latestRows(tables)
    .flatMap(({ account, row }): Bill[] => {
      const due = str(row.payment_due_date)
      if (!due) return []
      const balance = num(row.balance)
      const minimum = num(row.minimum_payment)
      // Nothing owed and nothing due: a card paid off or a credit balance.
      if ((balance === null || balance >= 0) && !minimum) return []
      const days = daysBetween(today, due)
      if (days > ahead || days < -behind) return []
      const autopay = str(row.autopay)
      const last4 = str(account.last4)
      return [
        {
          key: `${str(account.account_id)}|${due}`,
          account_id: str(account.account_id),
          name: `${billName(str(account.institution), str(account.name))}${last4 ? ` ··${last4}` : ''}`,
          kind: str(account.kind),
          due,
          days,
          minimum,
          owed: balance !== null && balance < 0 ? -balance : null,
          autopay: autopay === 'on' || autopay === 'off' ? autopay : null,
          statement: str(row.as_of),
        },
      ]
    })
    .sort((a, b) => a.due.localeCompare(b.due))
}

/**
 * The statements to re-read for due dates: the latest of each open card or
 * loan, where none of the bill fields was captured (read before they existed).
 */
export function missingDueDates(tables: { accounts: Row[]; balances: Row[] }): string[] {
  const ids = latestRows(tables)
    .filter(({ row }) => !str(row.payment_due_date) && num(row.minimum_payment) === null && !str(row.autopay))
    .map(({ row }) => str(row.source_file_id))
    .filter(Boolean)
  return [...new Set(ids)]
}

/**
 * One statement's balance rows with the bill fields filled in from a re-read,
 * matched by the account's last 4, or the only bill to the only row.
 */
export function withBills(rows: Row[], bills: Bills['bills'], last4Of: (accountId: string) => string): Row[] {
  return rows.map((row) => {
    const last4 = last4Of(str(row.account_id))
    const bill = bills.find((b) => b.account_last4 && b.account_last4 === last4) ?? (bills.length === 1 && rows.length === 1 ? bills[0] : undefined)
    if (!bill) return row
    return {
      ...row,
      payment_due_date: bill.payment_due_date,
      minimum_payment: bill.minimum_payment,
      autopay: bill.autopay === null ? null : bill.autopay ? 'on' : 'off',
    }
  })
}
